import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, filter, map, Observable, Subject, switchMap, take, takeUntil} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  BokkzAlgDataService, BokkzClient,
  BokkzDivDataService, BokkzDivLogic, ClientEntity,
  Dividendbelasting, DividendSpecificatie, FeatureServiceComponent,
  MenuInterface,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder} from "@angular/forms";
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-dividend-list',
  templateUrl: './feature-tax-dividend-list.component.html',
  styleUrl: './feature-tax-dividend-list.component.scss',
  imports: [
    SharedUiTableModule,
    SharedUiButtonModule,
    CommonModule,
    FeatureServiceComponent
  ]
})
export class FeatureTaxDividendListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<string>();
  private destroy$ = new Subject<void>();
  protected tableConfig!: TableConfiguration<any, any>;
  protected taxId: any;
  protected clientId: any;
  public showTable = false;
  public year = {} as number;
  public options$ = new BehaviorSubject<MenuInterface[]>([]);
  public options = [] as MenuInterface[];
  public aangifte = {} as Dividendbelasting;
  private clientEntity = {} as ClientEntity
  public totaalDividendUitkeringen = 0;
  public totaal = 0;
  public dialogVisible = false;
  public activePage = '';

  constructor(
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzDivDataService,
    public divLogic: BokkzDivLogic,
    private bokkzClient: BokkzClient,
    private cd: ChangeDetectorRef,
    private router: Router,
    public fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.clientId = params['clientId'];
    });
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((clientEntity)=> {
      this.clientEntity = clientEntity
    })
    this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.year = value;
      this.showTable = false;
      setTimeout(() => {
        this.createTableConfig();
        this.showTable = true;
      }, 0);
    });
    this.dataService.dividendTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((dividendTaxEntity) => {
      if (dividendTaxEntity.aangifte) {
        this.aangifte = dividendTaxEntity.aangifte;
        const totaal = dividendTaxEntity.aangifte?.aangifteDb?.verschuldigdeBelasting
        if (totaal) {
          this.totaal = totaal
        }
        const dividendUitkeringen: DividendSpecificatie[] = dividendTaxEntity.aangifte?.aangifteDb?.dividendSpecificaties ?? [];
        const totaalDividendUitkeringen: number = dividendUitkeringen
          .map(item => item.bedragDividenduitkering ?? 0)
          .reduce((totaal, bedrag) => totaal + bedrag, 0);
        if (totaalDividendUitkeringen) {
          this.totaalDividendUitkeringen = totaalDividendUitkeringen
        } else {
          this.totaalDividendUitkeringen = 0
          this.totaal = 0
        }
      }
    });
    this.dataService.menuInterface.pipe(takeUntil(this.destroy$)).subscribe((menuInterface) => {
      const old = this.options$.value;
      if (JSON.stringify(this.options$.value) != JSON.stringify(menuInterface)) {
        this.options$.next([...menuInterface.reverse()]);
        this.cd.detectChanges();
        for (let i = 0; i < menuInterface.length; i++) {
          if (JSON.stringify(old[i]) != JSON.stringify(menuInterface[i])) {
            dispatchSavedItemEvent({
              identifier: menuInterface[i].value,
              result: menuInterface[i] as unknown as Record<number, MenuInterface>
            });
            if (!menuInterface[i].visible) {
              dispatchDeletedItemEvent({
                identifier: menuInterface[i].value
              });
            }
          }
        }
      }
    });
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true
      },
      criteria: criteria,

      search: () => {
        return this.bokkzClient.getDividendTax(this.clientId, String(this.year)).pipe(
          switchMap((dividendTax) => {
            this.dataService.updateDividendTaxEntity(dividendTax);
            if (dividendTax.aangifte) {
              this.dataService.updateAangifte(dividendTax.aangifte);
              this.divLogic.getCalculation(dividendTax)
            } else {
              this.options$.next([])
              this.dataService.updateAantalValidaties(0)
            }
            return this.options$.pipe(
              filter((options) => Array.isArray(options)),
              map((options) => {
                const sortedGroups = options
                  .filter((item) => item.visible)
                  .map((group) => ({
                    ...group,
                    subonderdelen: (group.subonderdelen || [])
                      .filter((sub) => sub.visible)
                      .sort((a, b) => a.value - b.value)
                  }))
                  .sort((a, b) => a.value - b.value); // Sorteer hoofdgroepen ook
                return new SearchResult(sortedGroups, sortedGroups.length);
              }),
              take(1)
            );
          })
        );
      },

      details: (entity) => {
        if (entity != undefined) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen aangifte gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'De aangifte dividendbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig: this.year < 2024 ? undefined  : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  };

  navigateToSubPage(pageMapping: string): void {
    const url = '/tax-dividend/' + 'modules/' + this.clientId + '/' + pageMapping;
    this.router.navigate([url]).then(() => console.log('complete'));
    this.activePage = pageMapping;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
